/** Upsell only — cross_sale is intentionally excluded from row/flag marking. */
export const isUpsellSaleType = value =>
  String(value || '')
    .trim()
    .toLowerCase() === 'upsell';

export const orderHasUpsale = order => {
  if (typeof order?.hasUpsell === 'boolean') return order.hasUpsell;
  if (typeof order?.has_upsell === 'boolean') return order.has_upsell;
  const items = order?.lineItems || order?.line_items || [];
  return items.some(item => isUpsellSaleType(item.saleType || item.sale_type));
};

export const upsaleRowClass = order =>
  orderHasUpsale(order) ? 'upsale-row' : '';

export const normalizeOrderStatus = status =>
  String(status || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

/** Downstream fulfilment statuses shown as Approved in agent daily tables. */
const APPROVED_DISPLAY_STATUSES = new Set([
  'approved',
  'processing',
  'ready-to-go',
  'in-transit',
  'delivered',
]);

/** Profile/agents table display status: freeze approved+ as "approved". */
export const displayOrderStatus = status => {
  const normalized = normalizeOrderStatus(status);
  if (normalized === 'cancelled') return 'cancelled';
  if (normalized === 'pending') return 'pending';
  if (normalized === 'on-hold') return 'on-hold';
  if (APPROVED_DISPLAY_STATUSES.has(normalized)) return 'approved';
  return normalized;
};

/** Left-edge status strip (under invoice) for agent profile table rows. */
export const statusRowMarkClass = order => {
  const status = displayOrderStatus(order?.status);
  if (status === 'approved') return 'mark-approved';
  if (status === 'on-hold') return 'mark-on-hold';
  if (status === 'cancelled') return 'mark-cancelled';
  return '';
};

export const canChangeAssignment = status => {
  const normalized = normalizeOrderStatus(status);
  return normalized === 'pending' || normalized === 'on-hold';
};

export const isOrderUnassigned = order => {
  const assignment = order?.assignment;
  if (assignment?.unassignedAt || assignment?.unassigned_at) return true;
  return !order?.agent?.id;
};

export const isOrderCurrentlyAssigned = order => !isOrderUnassigned(order);

export const orderCallCount = order => {
  if (normalizeOrderStatus(order?.status) !== 'on-hold') return null;
  const tracking =
    order?.metadata?.callTracking || order?.metadata?.call_tracking || {};
  const count = Number(tracking.count || 0);
  return count > 0 ? count : null;
};

export const orderStatusBadgeClass = status => {
  const normalized = displayOrderStatus(status);
  if (normalized === 'on-hold') {
    return 'bg-amber-400/15 text-amber-300 border-amber-400/70';
  }
  if (normalized === 'cancelled') {
    return 'bg-red-500/15 text-red-400 border-red-500/70';
  }
  if (normalized === 'approved') {
    return 'bg-green-500/15 text-green-400 border-green-500/70';
  }
  if (normalized === 'pending') {
    return 'bg-n-alpha-2 text-n-slate-11 border-n-weak';
  }
  return 'bg-n-alpha-1 text-n-slate-10 border-n-weak';
};

export const orderInvoiceLabel = order =>
  order?.invoiceNumber || order?.invoice_number || order?.number || order?.id;

/** Local calendar YYYY-MM-DD (avoid UTC toISOString day-shift in BD / +offset zones). */
export const formatLocalDateKey = value => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const todayDateKey = () => formatLocalDateKey(new Date());

export const shiftLocalDateKey = (key, deltaDays) => {
  const base = String(key || todayDateKey()).slice(0, 10);
  const date = new Date(`${base}T12:00:00`);
  date.setDate(date.getDate() + deltaDays);
  return formatLocalDateKey(date);
};

export const emptyPerformanceDay = () => ({
  assigned: 0,
  approved: 0,
  approvedPlain: 0,
  upsale: 0,
  cancelled: 0,
  onHold: 0,
  pending: 0,
});

export const parsePerformanceDay = entry => ({
  assigned: Number(entry?.assigned || 0),
  approved: Number(entry?.approved || 0),
  approvedPlain: Number(entry?.approved_plain || entry?.approvedPlain || 0),
  upsale: Number(entry?.upsale || 0),
  cancelled: Number(entry?.cancelled || 0),
  onHold: Number(entry?.on_hold || entry?.onHold || 0),
  pending: Number(entry?.pending || 0),
});

/** Snapshot counts from the visible daily assigned rows (current status). */
export const performanceFromAssignedOrders = orders => {
  const acc = emptyPerformanceDay();
  const rows = Array.isArray(orders) ? orders : [];
  acc.assigned = rows.length;
  rows.forEach(order => {
    const status = displayOrderStatus(order?.status);
    if (status === 'approved') {
      acc.approved += 1;
      if (orderHasUpsale(order)) acc.upsale += 1;
      else acc.approvedPlain += 1;
    } else if (status === 'cancelled') {
      acc.cancelled += 1;
    } else if (status === 'on-hold') {
      acc.onHold += 1;
    } else if (status === 'pending') {
      acc.pending += 1;
    }
  });
  return acc;
};

export const orderReturnReceivedAt = order =>
  order?.returnReceivedAt || order?.return_received_at || null;

export const isReturnedAwaitingReceive = order =>
  normalizeOrderStatus(order?.status) === 'returned' &&
  !orderReturnReceivedAt(order);

export const isFlaggedAwaitingParcel = order =>
  normalizeOrderStatus(order?.status) === 'flagged';
